/**
 * Service de tarification équipe : le responsable d'équipe (index le plus bas) paie le tarif complet.
 */
class TeamPricingService {
    _buildGroupKey(id_course, equipe_name) {
        return `${id_course}|${equipe_name || ''}`;
    }

    /**
     * Construit une map des responsables d'équipe par course + nom d'équipe.
     * @param {Array} inscriptions
     * @returns {Map<string, number>} clé → index du responsable
     */
    buildLeaderMap(inscriptions) {
        const leaderMap = new Map();
        if (!inscriptions || !Array.isArray(inscriptions)) {
            return leaderMap;
        }

        for (let i = 0; i < inscriptions.length; i++) {
            const inscription = inscriptions[i];
            if (!inscription?.list_courses?.list) continue;

            const equipe_name = inscription.get_equipe_name ? inscription.get_equipe_name() : (inscription.coureur?.equipe_name || '');

            for (let j = 0; j < inscription.list_courses.list.length; j++) {
                const course = inscription.list_courses.list[j];
                if (!course.selected || !course.is_equipe_course?.()) continue;

                const key = this._buildGroupKey(course.id, equipe_name);
                const currentLeader = leaderMap.get(key);
                if (currentLeader === undefined || i < currentLeader) {
                    leaderMap.set(key, i);
                }
            }
        }

        return leaderMap;
    }

    isTeamPriceLeader(inscriptionIndex, id_course, equipe_name, leaderMap) {
        const key = this._buildGroupKey(id_course, equipe_name);
        const leaderIndex = leaderMap.get(key);
        if (leaderIndex === undefined) {
            return true;
        }
        return leaderIndex === inscriptionIndex;
    }

    /**
     * Contexte de tarification pour une inscription / course.
     * @param {number} inscriptionIndex
     * @param {Object} course
     * @param {string} equipe_name
     * @param {Map} leaderMap
     * @returns {{ is_team_member: boolean, is_leader: boolean }}
     */
    getPricingContext(inscriptionIndex, course, equipe_name, leaderMap) {
        if (!course?.is_equipe_course?.()) {
            return { is_team_member: false, is_leader: true, equipe_name: equipe_name || '' };
        }

        return {
            is_team_member: true,
            is_leader: this.isTeamPriceLeader(inscriptionIndex, course.id, equipe_name, leaderMap),
            equipe_name: equipe_name || '',
        };
    }

    /**
     * Contexte complet pour le calcul tarifaire équipe (inclut la liste des inscriptions).
     */
    buildFullPricingContext(pricingOptions, course) {
        if (!pricingOptions || !course?.selected || !course.is_equipe_course?.()) {
            return null;
        }

        return {
            ...this.getPricingContext(
                pricingOptions.inscriptionIndex,
                course,
                pricingOptions.equipe_name,
                pricingOptions.leaderMap
            ),
            inscriptions: pricingOptions.inscriptions || [],
        };
    }

    isCoureurLicensedFFA(coureur) {
        if (!coureur?.get_field_value_by_name) return false;

        const typeDocument = coureur.get_field_value_by_name("type_document");
        const licence = coureur.get_field_value_by_name("licence");

        return typeDocument === "Licence FFA" && licence !== "";
    }

    getTeamFFALicenceDiscountPerMember(tarif) {
        if (!tarif) return 0;

        if (typeof tarif.get_reduction_licence_ffa_equipe === "function") {
            return tarif.get_reduction_licence_ffa_equipe();
        }

        const metadata = typeof tarif.get_metadata_by_name === "function"
            ? tarif.get_metadata_by_name("reduction_licence_ffa_equipe")
            : tarif.metadatas?.find?.((m) => m.nom === "reduction_licence_ffa_equipe");

        if (!metadata?.valeur || metadata.valeur === "0") return 0;

        return parseFloat(metadata.valeur) || 0;
    }

    countTeamLicensedFFA(inscriptions, id_course, equipe_name) {
        if (!inscriptions?.length) return 0;

        let count = 0;

        for (let i = 0; i < inscriptions.length; i++) {
            const inscription = inscriptions[i];
            const course = inscription.list_courses?.list?.find(
                (c) => c.id == id_course && c.selected && c.is_equipe_course?.()
            );
            if (!course) continue;

            const insEquipeName = inscription.get_equipe_name
                ? inscription.get_equipe_name()
                : (inscription.coureur?.equipe_name || "");

            if (insEquipeName !== equipe_name) continue;

            if (this.isCoureurLicensedFFA(inscription.coureur)) {
                count++;
            }
        }

        return count;
    }

    /**
     * Applique une réduction cumulée sur le tarif équipe selon le nombre de licenciés FFA.
     */
    applyTeamFFALicenceDiscount(cart, course, pricingContext) {
        if (!cart?.is_team_price || !pricingContext?.is_leader) return;
        if (!pricingContext.inscriptions?.length) return;

        const tarif = course.tarifs?.list?.find((t) => t.id == cart.id_tarif);
        const discountPerMember = this.getTeamFFALicenceDiscountPerMember(tarif);
        if (discountPerMember <= 0) return;

        const nbLicensed = this.countTeamLicensedFFA(
            pricingContext.inscriptions,
            course.id,
            pricingContext.equipe_name ?? ''
        );
        if (nbLicensed <= 0) return;

        const discount = nbLicensed * discountPerMember;
        cart.prix = Math.max(0, parseFloat(cart.prix) - discount);
        cart.licence_ffa_team_discount = discount;
        cart.nb_licence_ffa_team_discount = nbLicensed;
    }

    /**
     * Applique le prix 0 aux membres non-responsables si le tarif est un tarif équipe.
     * @param {Object} cart
     * @param {Object} course
     * @param {Object} pricingContext
     */
    applyTeamPriceToCart(cart, course, pricingContext) {
        if (!cart?.id_tarif || !course?.tarifs?.list) return;

        const tarif = course.tarifs.list.find((t) => t.id == cart.id_tarif);
        if (!tarif?.is_team_price?.()) return;

        cart.is_team_price = true;

        if (pricingContext?.is_team_member && !pricingContext?.is_leader) {
            cart.prix = 0;
            cart.prix_regulier = 0;
        } else if (pricingContext?.is_leader) {
            this.applyTeamFFALicenceDiscount(cart, course, pricingContext);
        }
    }
}

export default new TeamPricingService();
